import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Switch,
  Button,
  IconButton,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  getExpiryRules,
  createExpiryRule,
  updateExpiryRule,
  deleteExpiryRule,
  getExpiryProtectedStatuses,
} from "../../../Services/Lead";
import { supabase } from "../../../supabaseClient";
import ConfirmationAlert from "../../../Subcomponents/ConfirmationAlert";
import { successTost, errorTost } from "../../../Services/Toast";
import cssClasses from "./RegenerationSettings.module.css";

// Project-scope editor: "All projects" / "Specific projects" + a checkbox
// multiselect. Defined at MODULE scope (not inside the parent component) so its
// identity is stable across re-renders — otherwise selecting a checkbox would
// remount the Autocomplete and close the dropdown, blocking multi-select.
function ProjectScopeEditor({ scope, projectNames, options, onScopeChange, onProjectsChange }) {
  return (
    <div className={cssClasses.cellStack}>
      <Select
        size="small"
        value={scope}
        onChange={(e) => onScopeChange(e.target.value)}
        sx={{
          fontFamily: "Inter-Regular",
          fontSize: "1.4rem",
          borderRadius: "8px",
          backgroundColor: "#fff",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#D0D5DD" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#98A2B3" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1570EF" },
          "& .MuiSelect-select": { padding: "0.8rem 1.1rem" },
        }}
      >
        <MenuItem value="all" style={{ fontSize: "1.4rem" }}>All projects</MenuItem>
        <MenuItem value="specific" style={{ fontSize: "1.4rem" }}>Specific projects</MenuItem>
      </Select>
      {scope === "specific" && (
        <>
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            limitTags={2}
            options={options}
            value={projectNames || []}
            onChange={(_, val) => onProjectsChange(val)}
            isOptionEqualToValue={(opt, val) => opt === val}
            ChipProps={{ size: "small", sx: { fontFamily: "Inter-Medium", fontSize: "1.2rem", maxWidth: "16rem" } }}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option} style={{ fontSize: "1.3rem" }}>
                <Checkbox
                  size="small"
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option}
              </li>
            )}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#fff",
                padding: "0.2rem 0.4rem",
                "& fieldset": { borderColor: "#D0D5DD" },
                "&:hover fieldset": { borderColor: "#98A2B3" },
                "&.Mui-focused fieldset": { borderColor: "#1570EF" },
              },
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder={projectNames?.length ? "" : "Select projects"} InputProps={{ ...params.InputProps, style: { fontSize: "1.3rem" } }} />
            )}
          />
          <span className={cssClasses.scopeHint}>Overrides the All-projects rule for the selected projects.</span>
        </>
      )}
    </div>
  );
}

// Admin tab to manage the daily lead-expiry rules. Each row is one stage rule;
// the daily backend job reads these at runtime (no deploy needed to change them).
//
// Project scope per rule:
//   - "All projects"      -> projectNames = []  (global rule)
//   - "Specific projects" -> projectNames = [..]  (applies only to those projects;
//                            overrides the global rule for those projects)
export default function RegenerationSettings() {
  const [rules, setRules] = useState([]);
  const [protectedStatuses, setProtectedStatuses] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  // Confirmation modal config: { title, subTitle, confirmLabel, onConfirm }
  const [confirmCfg, setConfirmCfg] = useState(null);

  // new-rule form
  const [newRule, setNewRule] = useState({
    stageName: "",
    eligibleStatuses: "",
    idleLimitDays: "",
    sortOrder: "",
    scope: "all",
    projectNames: [],
  });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rulesRes, protRes] = await Promise.all([getExpiryRules(), getExpiryProtectedStatuses()]);
      setRules(
        (rulesRes?.data || []).map((r) => {
          const projectNames = Array.isArray(r.projectNames) ? r.projectNames : [];
          return {
            ...r,
            projectNames,
            _scope: projectNames.length ? "specific" : "all",
            _eligibleText: Array.isArray(r.eligibleStatuses) ? r.eligibleStatuses.join(", ") : "",
          };
        })
      );
      setProtectedStatuses(protRes?.data || []);
    } catch (e) {
      errorTost("Failed to load regeneration settings.");
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const { data } = await supabase
        .from("Projects")
        .select("projectName")
        .eq("isVJ", true)
        .order("projectName", { ascending: true });
      setProjectOptions((data || []).map((d) => d.projectName).filter(Boolean));
    } catch (e) {
      // non-fatal; selector just shows no options
    }
  };

  useEffect(() => {
    load();
    loadProjects();
  }, []);

  const setRowField = (id, field, value) => {
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const setRowScope = (id, scope) => {
    setRules((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, _scope: scope, projectNames: scope === "all" ? [] : r.projectNames } : r
      )
    );
  };

  const parseStatuses = (text) =>
    String(text || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

  // Keep number fields non-negative (no minus). Allow empty while typing.
  const sanitizeNonNeg = (v) => {
    if (v === "" || v === null || v === undefined) return "";
    const n = Number(v);
    if (Number.isNaN(n) || n < 0) return "0";
    return v;
  };

  // Shared validation for a rule (used by both Save and Add).
  const validateRule = ({ stageName, idleLimitDays, sortOrder, scope, projectNames }) => {
    if (!String(stageName || "").trim()) return "Stage name is required.";
    if (idleLimitDays === "" || idleLimitDays === null || Number.isNaN(Number(idleLimitDays)))
      return "Idle limit (days) is required.";
    if (Number(idleLimitDays) < 0) return "Idle limit must be 0 or greater.";
    if (sortOrder !== "" && Number(sortOrder) < 0) return "Order must be 0 or greater.";
    if (scope === "specific" && (!projectNames || projectNames.length === 0))
      return "Select at least one project, or choose All projects.";
    return null;
  };

  // --- Save (with confirmation) ---
  const requestSave = (row) => {
    const msg = validateRule({
      stageName: row.stageName,
      idleLimitDays: row.idleLimitDays,
      sortOrder: row.sortOrder,
      scope: row._scope,
      projectNames: row.projectNames,
    });
    if (msg) { errorTost(msg); return; }
    const scopeText = row._scope === "specific"
      ? `projects: ${row.projectNames.join(", ")}`
      : "all projects";
    setConfirmCfg({
      title: "Save this rule?",
      subTitle: `Update the "${row.stageName}" rule (${scopeText}, ${row.idleLimitDays}-day idle limit). Changes take effect on the next daily run.`,
      confirmLabel: "Save",
      onConfirm: () => doSave(row),
    });
  };

  const doSave = async (row) => {
    setSavingId(row.id);
    try {
      const patch = {
        stageName: row.stageName.trim(),
        idleLimitDays: Number(row.idleLimitDays),
        eligibleStatuses: parseStatuses(row._eligibleText),
        projectNames: row._scope === "specific" ? row.projectNames : [],
        isActive: !!row.isActive,
        sortOrder: Number(row.sortOrder) || 0,
      };
      const res = await updateExpiryRule(row.id, patch);
      if (res?.success) successTost(`Saved "${row.stageName}".`);
      else errorTost(res?.error || "Save failed.");
    } catch (e) {
      errorTost(e?.response?.data?.error || "Save failed.");
    } finally {
      setSavingId(null);
    }
  };

  // --- Delete (with confirmation) ---
  const requestDelete = (row) => {
    setConfirmCfg({
      title: "Delete this rule?",
      subTitle: `This permanently removes the "${row.stageName || "untitled"}" rule. The daily job will no longer apply it.`,
      confirmLabel: "Delete",
      onConfirm: () => doDelete(row),
    });
  };

  const doDelete = async (row) => {
    try {
      const res = await deleteExpiryRule(row.id);
      if (res?.success) {
        successTost(`Deleted "${row.stageName}".`);
        load();
      } else {
        errorTost(res?.error || "Delete failed.");
      }
    } catch (e) {
      errorTost(e?.response?.data?.error || "Delete failed.");
    }
  };

  // --- Add (no confirmation — explicit "Add rule" action) ---
  const addRule = async () => {
    const msg = validateRule({
      stageName: newRule.stageName,
      idleLimitDays: newRule.idleLimitDays,
      sortOrder: newRule.sortOrder,
      scope: newRule.scope,
      projectNames: newRule.projectNames,
    });
    if (msg) { errorTost(msg); return; }
    setCreating(true);
    try {
      const res = await createExpiryRule({
        stageName: newRule.stageName.trim(),
        eligibleStatuses: parseStatuses(newRule.eligibleStatuses),
        projectNames: newRule.scope === "specific" ? newRule.projectNames : [],
        idleLimitDays: Number(newRule.idleLimitDays),
        sortOrder: Number(newRule.sortOrder) || 0,
        isActive: true,
      });
      if (res?.success) {
        successTost(`Added "${newRule.stageName}".`);
        setNewRule({ stageName: "", eligibleStatuses: "", idleLimitDays: "", sortOrder: "", scope: "all", projectNames: [] });
        load();
      } else {
        errorTost(res?.error || "Create failed.");
      }
    } catch (e) {
      errorTost(e?.response?.data?.error || "Create failed.");
    } finally {
      setCreating(false);
    }
  };

  const handleConfirm = (value) => {
    if (value === "confirmed" && confirmCfg?.onConfirm) confirmCfg.onConfirm();
    setConfirmCfg(null);
  };

  // ----- shared MUI theming (Inter, #EAECF0, #F9FAFB, #1570EF) -----
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      fontFamily: "Inter-Regular",
      fontSize: "1.4rem",
      borderRadius: "8px",
      backgroundColor: "#fff",
      "& fieldset": { borderColor: "#D0D5DD" },
      "&:hover fieldset": { borderColor: "#98A2B3" },
      "&.Mui-focused fieldset": { borderColor: "#1570EF", borderWidth: "1px" },
    },
    "& .MuiInputBase-input": { padding: "0.9rem 1.1rem" },
  };

  const tableSx = {
    minWidth: 980,
    "& .MuiTableCell-head": {
      backgroundColor: "#F9FAFB",
      color: "#667085",
      fontFamily: "Inter-Medium",
      fontSize: "1.2rem",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.04rem",
      borderBottom: "1px solid #EAECF0",
      padding: "1.2rem 1.6rem",
      whiteSpace: "nowrap",
    },
    "& .MuiTableCell-body": {
      borderBottom: "1px solid #F2F4F7",
      padding: "1.2rem 1.6rem",
      verticalAlign: "top",
    },
    "& .MuiTableRow-root:last-child .MuiTableCell-body": { borderBottom: "none" },
    "& .addRow .MuiTableCell-body": { backgroundColor: "#FCFCFD" },
  };

  const saveBtnSx = {
    textTransform: "none",
    fontFamily: "Inter-Medium",
    fontSize: "1.3rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    minWidth: "7.2rem",
    boxShadow: "none",
    backgroundColor: "#1570EF",
    borderRadius: "8px",
    px: "1.6rem",
    "&:hover": { backgroundColor: "#175CD3", boxShadow: "none" },
  };

  const addBtnSx = {
    textTransform: "none",
    fontFamily: "Inter-Medium",
    fontSize: "1.3rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    minWidth: "9rem",
    borderRadius: "8px",
    borderColor: "#1570EF",
    color: "#1570EF",
    "&:hover": { borderColor: "#175CD3", backgroundColor: "#EFF8FF" },
  };

  const deleteBtnSx = {
    border: "1px solid #FDA29B",
    borderRadius: "8px",
    color: "#B42318",
    padding: "0.7rem",
    "&:hover": { backgroundColor: "#FEF3F2", borderColor: "#F97066" },
  };

  return (
    <div className={cssClasses.wrapper}>
      <div className={cssClasses.header}>
        <h3 className={cssClasses.heading}>Regeneration Settings</h3>
        <p className={cssClasses.subheading}>
          The daily job marks idle leads <b>Lost</b> per these stage rules. Set a rule to <b>All projects</b> or scope it to
          <b> specific projects</b>; a project with its own rule ignores the matching All-projects rule. Changes apply on the next run — no deploy needed.
        </p>
      </div>

      {loading ? (
        <div className={cssClasses.loading}><CircularProgress /></div>
      ) : (
        <div className={cssClasses.tableCard}>
          <div className={cssClasses.tableScroll}>
            <Table size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Stage</TableCell>
                  <TableCell>Eligible statuses</TableCell>
                  <TableCell>Projects</TableCell>
                  <TableCell align="center">Idle limit (days)</TableCell>
                  <TableCell align="center">Order</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <TextField variant="outlined" size="small" fullWidth value={row.stageName} onChange={(e) => setRowField(row.id, "stageName", e.target.value)} sx={{ ...fieldSx, minWidth: 130 }} />
                    </TableCell>
                    <TableCell>
                      <TextField variant="outlined" size="small" fullWidth value={row._eligibleText} onChange={(e) => setRowField(row.id, "_eligibleText", e.target.value)} placeholder="e.g. Unclaimed, UnClaimed" sx={{ ...fieldSx, minWidth: 180 }} />
                    </TableCell>
                    <TableCell>
                      <ProjectScopeEditor
                        scope={row._scope}
                        projectNames={row.projectNames}
                        options={projectOptions}
                        onScopeChange={(v) => setRowScope(row.id, v)}
                        onProjectsChange={(val) => setRowField(row.id, "projectNames", val)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField variant="outlined" size="small" type="number" value={row.idleLimitDays} inputProps={{ min: 0 }} onChange={(e) => setRowField(row.id, "idleLimitDays", sanitizeNonNeg(e.target.value))} sx={{ ...fieldSx, width: 96 }} />
                    </TableCell>
                    <TableCell align="center">
                      <TextField variant="outlined" size="small" type="number" value={row.sortOrder} inputProps={{ min: 0 }} onChange={(e) => setRowField(row.id, "sortOrder", sanitizeNonNeg(e.target.value))} sx={{ ...fieldSx, width: 80 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Switch checked={!!row.isActive} onChange={(e) => setRowField(row.id, "isActive", e.target.checked)} />
                    </TableCell>
                    <TableCell align="center">
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <Button size="small" variant="contained" onClick={() => requestSave(row)} disabled={savingId === row.id} sx={saveBtnSx}>
                          {savingId === row.id ? "Saving…" : "Save"}
                        </Button>
                        <Tooltip title="Delete rule">
                          <IconButton size="small" onClick={() => requestDelete(row)} sx={deleteBtnSx}>
                            <DeleteOutlineIcon style={{ fontSize: "2rem" }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Add-rule row */}
                <TableRow className="addRow">
                  <TableCell>
                    <TextField variant="outlined" size="small" fullWidth placeholder="New stage" value={newRule.stageName} onChange={(e) => setNewRule({ ...newRule, stageName: e.target.value })} sx={{ ...fieldSx, minWidth: 130 }} />
                  </TableCell>
                  <TableCell>
                    <TextField variant="outlined" size="small" fullWidth placeholder="statuses, comma-separated" value={newRule.eligibleStatuses} onChange={(e) => setNewRule({ ...newRule, eligibleStatuses: e.target.value })} sx={{ ...fieldSx, minWidth: 180 }} />
                  </TableCell>
                  <TableCell>
                    <ProjectScopeEditor
                      scope={newRule.scope}
                      projectNames={newRule.projectNames}
                      options={projectOptions}
                      onScopeChange={(v) => setNewRule({ ...newRule, scope: v, projectNames: v === "all" ? [] : newRule.projectNames })}
                      onProjectsChange={(val) => setNewRule({ ...newRule, projectNames: val })}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField variant="outlined" size="small" type="number" placeholder="days" value={newRule.idleLimitDays} inputProps={{ min: 0 }} onChange={(e) => setNewRule({ ...newRule, idleLimitDays: sanitizeNonNeg(e.target.value) })} sx={{ ...fieldSx, width: 96 }} />
                  </TableCell>
                  <TableCell align="center">
                    <TextField variant="outlined" size="small" type="number" placeholder="0" value={newRule.sortOrder} inputProps={{ min: 0 }} onChange={(e) => setNewRule({ ...newRule, sortOrder: sanitizeNonNeg(e.target.value) })} sx={{ ...fieldSx, width: 80 }} />
                  </TableCell>
                  <TableCell align="center">—</TableCell>
                  <TableCell align="center">
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addRule} disabled={creating} sx={addBtnSx}>
                      {creating ? "Adding…" : "Add rule"}
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Protected statuses (read-only reference) */}
      <div className={cssClasses.protectedWrap}>
        <div className={cssClasses.protectedTitle}>Protected statuses (never marked Lost)</div>
        <div className={cssClasses.protectedChips}>
          {protectedStatuses.length === 0 ? (
            <span className={cssClasses.protectedEmpty}>none</span>
          ) : (
            protectedStatuses.map((p) => (
              <Chip
                key={p.id || p.status}
                label={`${p.status}`}
                size="small"
                sx={{ fontFamily: "Inter-Medium", fontSize: "1.2rem", backgroundColor: "#F2F4F7", color: "#344054" }}
              />
            ))
          )}
        </div>
      </div>

      {confirmCfg && (
        <ConfirmationAlert
          open={!!confirmCfg}
          close={() => setConfirmCfg(null)}
          title={confirmCfg.title}
          subTitle={confirmCfg.subTitle}
          buttonTitle="Cancel"
          returnRespButton={confirmCfg.confirmLabel}
          confirm={handleConfirm}
          hideCloseIcon
        />
      )}
    </div>
  );
}
